import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthGuard } from '@/components/auth/AuthGuard';
import { AdminGuard } from '@/components/auth/AdminGuard';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { PageLoader } from '@/components/ui/PageLoader';
import { ChunkLoadErrorBoundary, clearChunkReloadFlag } from '@/components/ChunkLoadErrorBoundary';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { OwnerOnlyRoute } from '@/components/OwnerOnlyRoute';
import { StarterGate } from '@/components/StarterGate';
import { useMe } from '@/hooks';
import { useAuthStore } from '@/stores/authStore';

/**
 * Route tree for the Merkflow SPA.
 *
 * Layout:
 *   Public:    /login, /signup, /auth/*
 *   Protected (AuthGuard):
 *     Standalone:  /, /onboarding/*, /suspended
 *     Dashboard (DashboardLayout): /dashboard/*
 *     Admin (AdminLayout + AdminGuard): /admin/*
 *
 * Legacy paths `/auth/signin` and `/auth/register` are still mapped to the
 * login/signup pages so any stale in-app redirects keep working.
 */

// ----- Public / auth -----
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const SignupPage = lazy(() => import('@/pages/auth/SignupPage'));
const AuthCallbackPage = lazy(() => import('@/pages/auth/AuthCallbackPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage'));

// ----- Standalone authenticated -----
const RootRedirectPage = lazy(() => import('@/pages/RootRedirectPage'));
const SuspendedPage = lazy(() => import('@/pages/SuspendedPage'));
const WaitlistedPage = lazy(() => import('@/pages/WaitlistedPage'));
const AcceptInvitePage = lazy(() => import('@/pages/AcceptInvitePage'));
const OnboardingIndexPage = lazy(() => import('@/pages/onboarding/OnboardingIndexPage'));
const OnboardingWizardPage = lazy(() => import('@/pages/onboarding/OnboardingWizardPage'));
const OnboardingPlanPage = lazy(() => import('@/pages/onboarding/OnboardingPlanPage'));

// ----- Dashboard -----
const OverviewPage = lazy(() => import('@/pages/dashboard/OverviewPage'));
const ReviewPage = lazy(() => import('@/pages/dashboard/ReviewPage'));
const BrandsPage = lazy(() => import('@/pages/dashboard/BrandsPage'));
const BrandDetailPage = lazy(() => import('@/pages/dashboard/brands/BrandDetailPage'));
const BrandPostsPage = lazy(() => import('@/pages/dashboard/brands/BrandPostsPage'));
const CarouselDesignerPage = lazy(() => import('@/pages/dashboard/CarouselDesignerPage'));
const CarouselTemplatesSettingsPage = lazy(
  () => import('@/pages/dashboard/settings/CarouselTemplatesPage'),
);
const AiVisibilityPage = lazy(() => import('@/pages/dashboard/AiVisibilityPage'));
const AnalyticsPage = lazy(() => import('@/pages/dashboard/AnalyticsPage'));
const EngagementPage = lazy(() => import('@/pages/dashboard/EngagementPage'));
const AdsPage = lazy(() => import('@/pages/dashboard/AdsPage'));
const AdCampaignDetailPage = lazy(() => import('@/pages/dashboard/AdCampaignDetailPage'));
const PostsPage = lazy(() => import('@/pages/dashboard/PostsPage'));
const StorylinePage = lazy(() => import('@/pages/dashboard/StorylinePage'));
const NewStorylinePage = lazy(() => import('@/pages/dashboard/NewStorylinePage'));
const StorylineDetailPage = lazy(() => import('@/pages/dashboard/StorylineDetailPage'));
const CalendarPage = lazy(() => import('@/pages/dashboard/CalendarPage'));
const DiscoverPage = lazy(() => import('@/pages/dashboard/DiscoverPage'));
const BlogPostsPage = lazy(() => import('@/pages/dashboard/BlogPostsPage'));
const UsagePage = lazy(() => import('@/pages/dashboard/UsagePage'));

// ----- Settings -----
const SettingsPage = lazy(() => import('@/pages/dashboard/settings/SettingsPage'));
const ProfilePage = lazy(() => import('@/pages/dashboard/settings/ProfilePage'));
const MembersPage = lazy(() => import('@/pages/dashboard/settings/MembersPage'));
const ConnectionsPage = lazy(() => import('@/pages/dashboard/settings/ConnectionsPage'));
const ConnectedAppsPage = lazy(() => import('@/pages/dashboard/settings/ConnectedAppsPage'));
const CadencePage = lazy(() => import('@/pages/dashboard/settings/CadencePage'));
const ReportSchedulesPage = lazy(() => import('@/pages/dashboard/settings/ReportSchedulesPage'));
const AutoResponsesPage = lazy(() => import('@/pages/dashboard/settings/AutoResponsesPage'));
const BillingPage = lazy(() => import('@/pages/dashboard/settings/BillingPage'));
const PrivacyPage = lazy(() => import('@/pages/dashboard/settings/PrivacyPage'));

// ----- Admin -----
const AdminIndexPage = lazy(() => import('@/pages/admin/AdminIndexPage'));
const AdminUsers = lazy(() => import('@/pages/admin/UsersPage'));
const AdminWorkspaces = lazy(() => import('@/pages/admin/WorkspacesPage'));
const AdminUsage = lazy(() => import('@/pages/admin/UsagePage'));
const AdminAi = lazy(() => import('@/pages/admin/AiSettingsPage'));
const AdminIntegrations = lazy(() => import('@/pages/admin/IntegrationsPage'));
const AdminPlans = lazy(() => import('@/pages/admin/PlansPage'));
const AdminEmail = lazy(() => import('@/pages/admin/EmailSettingsPage'));
const AdminPlatformSettings = lazy(() => import('@/pages/admin/PlatformSettingsPage'));
const AdminStyleGuardrails = lazy(() => import('@/pages/admin/StyleGuardrailsPage'));
const AdminGenerationTraces = lazy(() => import('@/pages/admin/GenerationTracesPage'));
const AdminPlatform = lazy(() => import('@/pages/admin/PlatformPage'));
const AdminSchedulers = lazy(() => import('@/pages/admin/SchedulersPage'));
const AdminMockPlatform = lazy(() => import('@/pages/admin/MockPlatformPage'));
const AdminS3Browser = lazy(() => import('@/pages/admin/S3BrowserPage'));
const AdminRag = lazy(() => import('@/pages/admin/RagDashboardPage'));
const AdminWaitlist = lazy(() => import('@/pages/admin/WaitlistPage'));
const AdminFeedback = lazy(() => import('@/pages/admin/FeedbackPage'));
const AdminFeedbackDetail = lazy(() => import('@/pages/admin/FeedbackDetailPage'));
const AdminInviteLinks = lazy(() => import('@/pages/admin/InviteLinksPage'));
const JoinPage = lazy(() => import('@/pages/JoinPage'));

export function App() {
  // Once we've rendered successfully after a reload, clear the loop-guard
  // flag so the NEXT deploy can also recover automatically. The boundary
  // sets the flag right before reload; this clears it on first paint of
  // the post-reload session.
  useEffect(() => {
    clearChunkReloadFlag();
  }, []);

  return (
    <ChunkLoadErrorBoundary>
      <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <AccentColorEffect />
          <Routes>
            {/* Public */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/auth/signin" element={<LoginPage />} />
            <Route path="/auth/register" element={<SignupPage />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
            {/* Accept-invite is intentionally public — the preview endpoint works
            without a JWT, and the page itself handles the "sign in to accept"
            redirect when the user isn't logged in. */}
            <Route path="/invite/:token" element={<AcceptInvitePage />} />
            <Route path="/join/:code" element={<JoinPage />} />

            {/* Protected */}
            <Route element={<AuthGuard />}>
              {/* Standalone (no dashboard chrome) */}
              <Route path="/" element={<RootRedirectPage />} />
              <Route path="/suspended" element={<SuspendedPage />} />
              <Route path="/waitlisted" element={<WaitlistedPage />} />
              <Route path="/onboarding" element={<OnboardingIndexPage />} />
              <Route path="/onboarding/wizard" element={<OnboardingWizardPage />} />
              <Route path="/onboarding/plan" element={<OnboardingPlanPage />} />

              {/* Dashboard shell */}
              <Route element={<DashboardLayout />}>
                <Route path="/dashboard" element={<OverviewPage />} />
                <Route path="/dashboard/review" element={<ReviewPage />} />
                <Route path="/dashboard/brands" element={<BrandsPage />} />
                <Route path="/dashboard/brands/:brandId" element={<BrandDetailPage />} />
                <Route path="/dashboard/brands/:brandId/posts" element={<BrandPostsPage />} />
                <Route path="/dashboard/carousel-designer" element={<CarouselDesignerPage />} />
                <Route
                  path="/dashboard/ai-visibility"
                  element={
                    <StarterGate
                      titleKey="starterGate.aiVisibility.title"
                      descriptionKey="starterGate.aiVisibility.description"
                    >
                      <AiVisibilityPage />
                    </StarterGate>
                  }
                />
                <Route
                  path="/dashboard/analytics"
                  element={
                    <StarterGate
                      titleKey="starterGate.analytics.title"
                      descriptionKey="starterGate.analytics.description"
                    >
                      <AnalyticsPage />
                    </StarterGate>
                  }
                />
                <Route path="/dashboard/engagement" element={<EngagementPage />} />
                <Route
                  path="/dashboard/ads"
                  element={
                    <StarterGate
                      titleKey="starterGate.ads.title"
                      descriptionKey="starterGate.ads.description"
                    >
                      <AdsPage />
                    </StarterGate>
                  }
                />
                <Route
                  path="/dashboard/ads/:campaignId"
                  element={
                    <StarterGate
                      titleKey="starterGate.ads.title"
                      descriptionKey="starterGate.ads.description"
                    >
                      <AdCampaignDetailPage />
                    </StarterGate>
                  }
                />
                <Route path="/dashboard/posts" element={<PostsPage />} />
                <Route
                  path="/dashboard/storylines"
                  element={
                    <StarterGate
                      titleKey="starterGate.storylines.title"
                      descriptionKey="starterGate.storylines.description"
                    >
                      <StorylinePage />
                    </StarterGate>
                  }
                />
                <Route
                  path="/dashboard/storylines/new"
                  element={
                    <StarterGate
                      titleKey="starterGate.storylines.title"
                      descriptionKey="starterGate.storylines.description"
                    >
                      <NewStorylinePage />
                    </StarterGate>
                  }
                />
                <Route
                  path="/dashboard/storylines/:storylineId"
                  element={
                    <StarterGate
                      titleKey="starterGate.storylines.title"
                      descriptionKey="starterGate.storylines.description"
                    >
                      <StorylineDetailPage />
                    </StarterGate>
                  }
                />
                <Route
                  path="/dashboard/calendar"
                  element={
                    <StarterGate
                      titleKey="starterGate.calendar.title"
                      descriptionKey="starterGate.calendar.description"
                    >
                      <CalendarPage />
                    </StarterGate>
                  }
                />
                <Route
                  path="/dashboard/discover"
                  element={
                    <StarterGate
                      titleKey="starterGate.discover.title"
                      descriptionKey="starterGate.discover.description"
                    >
                      <DiscoverPage />
                    </StarterGate>
                  }
                />
                <Route
                  path="/dashboard/blog"
                  element={
                    <StarterGate
                      titleKey="starterGate.blog.title"
                      descriptionKey="starterGate.blog.description"
                    >
                      <BlogPostsPage />
                    </StarterGate>
                  }
                />
                <Route
                  path="/dashboard/usage"
                  element={
                    <OwnerOnlyRoute>
                      <StarterGate
                        titleKey="starterGate.usage.title"
                        descriptionKey="starterGate.usage.description"
                      >
                        <UsagePage />
                      </StarterGate>
                    </OwnerOnlyRoute>
                  }
                />
                <Route path="/dashboard/settings" element={<SettingsPage />} />
                <Route path="/dashboard/settings/profile" element={<ProfilePage />} />
                <Route
                  path="/dashboard/settings/members"
                  element={
                    <OwnerOnlyRoute>
                      <MembersPage />
                    </OwnerOnlyRoute>
                  }
                />
                <Route
                  path="/dashboard/settings/connections"
                  element={
                    <OwnerOnlyRoute>
                      <ConnectionsPage />
                    </OwnerOnlyRoute>
                  }
                />
                <Route
                  path="/dashboard/settings/connected-apps"
                  element={
                    <OwnerOnlyRoute>
                      <ConnectedAppsPage />
                    </OwnerOnlyRoute>
                  }
                />
                <Route path="/dashboard/settings/cadence" element={<CadencePage />} />
                <Route
                  path="/dashboard/settings/carousel-templates"
                  element={
                    <OwnerOnlyRoute>
                      <CarouselTemplatesSettingsPage />
                    </OwnerOnlyRoute>
                  }
                />
                <Route
                  path="/dashboard/settings/reports"
                  element={
                    <OwnerOnlyRoute>
                      <ReportSchedulesPage />
                    </OwnerOnlyRoute>
                  }
                />
                <Route
                  path="/dashboard/settings/auto-responses"
                  element={
                    <OwnerOnlyRoute>
                      <AutoResponsesPage />
                    </OwnerOnlyRoute>
                  }
                />
                <Route
                  path="/dashboard/settings/billing"
                  element={
                    <OwnerOnlyRoute>
                      <BillingPage />
                    </OwnerOnlyRoute>
                  }
                />
                <Route path="/dashboard/settings/privacy" element={<PrivacyPage />} />
              </Route>

              {/* Admin shell */}
              <Route element={<AdminGuard />}>
                <Route element={<AdminLayout />}>
                  <Route path="/admin" element={<AdminIndexPage />} />
                  <Route path="/admin/users" element={<AdminUsers />} />
                  <Route path="/admin/workspaces" element={<AdminWorkspaces />} />
                  <Route path="/admin/usage" element={<AdminUsage />} />
                  <Route path="/admin/ai-settings" element={<AdminAi />} />
                  <Route path="/admin/integrations" element={<AdminIntegrations />} />
                  <Route path="/admin/plans" element={<AdminPlans />} />
                  <Route path="/admin/email" element={<AdminEmail />} />
                  <Route path="/admin/platform-settings" element={<AdminPlatformSettings />} />
                  <Route path="/admin/style-guardrails" element={<AdminStyleGuardrails />} />
                  <Route path="/admin/generation-traces" element={<AdminGenerationTraces />} />
                  <Route path="/admin/platform" element={<AdminPlatform />} />
                  <Route path="/admin/schedulers" element={<AdminSchedulers />} />
                  <Route path="/admin/mock-platform" element={<AdminMockPlatform />} />
                  <Route path="/admin/s3" element={<AdminS3Browser />} />
                  <Route path="/admin/rag" element={<AdminRag />} />
                  <Route path="/admin/waitlist" element={<AdminWaitlist />} />
                  <Route path="/admin/feedback" element={<AdminFeedback />} />
                  <Route path="/admin/feedback/:id" element={<AdminFeedbackDetail />} />
                  <Route path="/admin/invite-links" element={<AdminInviteLinks />} />
                </Route>
              </Route>
            </Route>

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </ChunkLoadErrorBoundary>
  );
}

/**
 * Polish step 3 — applies the user's `accentColor` preference to the
 * `--color-accent` CSS variable on `<html>`. Every Tailwind utility that
 * resolves through the `accent` token (`bg-accent`, `text-accent`,
 * `border-accent`, the focus ring) reads from `--color-accent`; the
 * hover / light / dark shades derive from it via color-mix in
 * tailwind-theme.css so setting one value updates them all. Mounted
 * once at the top of the route tree so it works regardless of which
 * page the user lands on. Skipped when the user isn't signed in
 * (useMe is gated on the JWT).
 *
 * Pre-2026-05-13 this set `--mk-accent` which nothing in the
 * stylesheet reads — the accent picker silently no-op'd in the UI.
 */
function AccentColorEffect() {
  // Only fire useMe when there's a JWT in the store — otherwise we'd
  // 401 every public-page render. The hook is guarded on `enabled` so
  // the query never runs without auth.
  const token = useAuthStore((s) => s.token);
  const { data } = useMe(token != null);
  useEffect(() => {
    const accent = data?.accentColor;
    if (!accent) return;
    document.documentElement.style.setProperty('--color-accent', accent);
    // Legacy alias — kept in sync for any callsite that still reads
    // the old name. No callsites in tree today; cheap insurance.
    document.documentElement.style.setProperty('--mk-accent', accent);
  }, [data?.accentColor]);
  return null;
}
